import React, { createContext, useContext, useMemo, type FC, type PropsWithChildren } from 'react';
import type { CustomComponents } from '../../common/types.tsx';

const CustomComponentsContext = createContext<CustomComponents | null>(null);

export type CustomComponentsProviderProps = PropsWithChildren<{
	components?: CustomComponents;
}>;

export const CustomComponentsProvider: FC<CustomComponentsProviderProps> = ({
	children,
	components,
}) => {
	// Memoize individual components to avoid re-rendering if a new object with
	// the same values is passed in via NativeIssueTable’s components prop
	const columnPicker = useMemo(
		() => ({
			Container: components?.columnPicker?.Container,
			EmptyState: components?.columnPicker?.EmptyState,
			Footer: components?.columnPicker?.Footer,
			Icon: components?.columnPicker?.Icon,
			PopupComponent: components?.columnPicker?.PopupComponent,
		}),
		[
			components?.columnPicker?.Container,
			components?.columnPicker?.EmptyState,
			components?.columnPicker?.Footer,
			components?.columnPicker?.Icon,
			components?.columnPicker?.PopupComponent,
		],
	);

	const value = useMemo(
		() => ({
			ConnectionsSubscriber: components?.ConnectionsSubscriber,
			Footer: components?.Footer,
			IssueCount: components?.IssueCount,
			NoColumns: components?.NoColumns,
			NoIssues: components?.NoIssues,
			columnPicker,
		}),
		[
			columnPicker,
			components?.ConnectionsSubscriber,
			components?.Footer,
			components?.IssueCount,
			components?.NoColumns,
			components?.NoIssues,
		],
	);

	return (
		<CustomComponentsContext.Provider value={value}>{children}</CustomComponentsContext.Provider>
	);
};

export const useCustomComponents = () => {
	const context = useContext(CustomComponentsContext);

	if (!context) {
		throw new Error('useCustomComponents must be used within a CustomComponentsProvider');
	}

	return context;
};
